import React, { useState } from 'react';
import { playCorrect, playIncorrect, playComplete } from '../../utils/sounds';

/**
 * ExerciseRunner — sequential runner for 1 Exercise's questions.
 * Handles different question shapes (text answer, options, accepted_answers).
 * Props:
 *   exercise: { id, title, exercise_type, content_data, questions: [...] }
 *   onComplete: (results: [{question_id, user_answer, is_correct}]) => void
 */
const ExerciseRunner = ({ exercise, onComplete }) => {
  const questions = exercise?.questions || [];
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState('');
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);  // null | {correct, expected}
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);

  if (!exercise || questions.length === 0) {
    return (
      <div className="hp-card text-center">
        <p className="font-headline font-black text-hp-on-surface">Bài tập trống</p>
      </div>
    );
  }

  if (done) {
    const correct = results.filter(r => r.is_correct).length;
    const total = results.length;
    const pct = Math.round((correct / total) * 100);
    return (
      <div className="hp-card hp-card--featured hp-card--accent-gold text-center">
        <div className="text-6xl mb-3">{pct >= 80 ? '🏆' : pct >= 50 ? '💪' : '📚'}</div>
        <h2 className="font-headline font-black text-3xl text-hp-on-surface mb-3">
          {correct}/{total} đúng ({pct}%)
        </h2>
        <p className="text-hp-on-surface/70 mb-5">
          {pct >= 80 ? 'Xuất sắc!' : pct >= 50 ? 'Khá tốt!' : 'Cần ôn thêm.'}
        </p>
        <button
          onClick={() => onComplete?.(results)}
          className="hp-button-gradient text-white px-8 py-3 rounded-xl font-headline font-black shadow-lg hover:-translate-y-1 active:scale-95 transition-all"
        >
          Hoàn thành →
        </button>
      </div>
    );
  }

  const q = questions[idx];
  const total = questions.length;

  // Extract options (may be stored various ways)
  const options = q.options || exercise.content_data?.word_bank || exercise.content_data?.answer_bank || null;
  const hasOptions = Array.isArray(options) && options.length > 0;

  const evaluate = (userAnswer) => {
    // Check against accepted_answers (JSON list/dict)
    let expected = q.accepted_answers;
    if (Array.isArray(expected)) {
      const correct = expected.some(a => String(a).toLowerCase().trim() === String(userAnswer).toLowerCase().trim());
      return { correct, expected: expected.join(' / ') };
    }
    if (expected && typeof expected === 'object') {
      const allVals = Object.values(expected).flat();
      const correct = allVals.some(a => String(a).toLowerCase().trim() === String(userAnswer).toLowerCase().trim());
      return { correct, expected: allVals.join(' / ') };
    }
    if (typeof expected === 'string') {
      return { correct: String(userAnswer).toLowerCase().trim() === expected.toLowerCase().trim(), expected };
    }
    return { correct: false, expected: '—' };
  };

  const handleSubmit = () => {
    const userAnswer = selectedOpt ?? input;
    if (!userAnswer) return;
    const { correct, expected } = evaluate(userAnswer);
    if (correct) playCorrect(); else playIncorrect();
    setShowFeedback({ correct, expected });
    setResults(prev => [...prev, { question_id: q.id, user_answer: String(userAnswer), is_correct: correct }]);
  };

  const handleNext = () => {
    setShowFeedback(null);
    setInput('');
    setSelectedOpt(null);
    if (idx + 1 >= total) {
      playComplete();
      setDone(true);
    } else {
      setIdx(idx + 1);
    }
  };

  // Context (shared across questions of same exercise)
  const ctx = exercise.content_data || {};
  const storyText = ctx.story_text || ctx.context_text;
  const imageUrl = ctx.image_url || ctx.context_image_url;

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-hupath-scout rounded-full transition-all duration-500"
               style={{ width: `${(idx / total) * 100}%` }} />
        </div>
        <span className="text-sm font-bold text-hp-on-surface/60 font-headline">
          Câu {idx + 1}/{total}
        </span>
      </div>

      {/* Exercise context (shown once at top) */}
      {(storyText || imageUrl) && (
        <div className="hp-card hp-card--compact bg-gradient-to-br from-amber-50 to-amber-100">
          {imageUrl && (
            <img
              src={imageUrl.startsWith('http') ? imageUrl : `${(process.env.REACT_APP_API_URL || '').replace('/api/v1', '')}${imageUrl}`}
              alt="context"
              className="w-full max-h-48 object-contain rounded-lg mb-2"
            />
          )}
          {storyText && (
            <p className="text-sm text-hp-on-surface/80 italic">"{storyText}"</p>
          )}
        </div>
      )}

      {/* Question */}
      <div className="hp-card">
        <p className="text-lg font-bold text-hp-on-surface mb-3">{q.question_text || '(no text)'}</p>

        {hasOptions ? (
          <div className="flex flex-wrap gap-2">
            {options.map((opt, i) => {
              const optText = typeof opt === 'string' ? opt : (opt.text || opt.word || JSON.stringify(opt));
              const isSelected = selectedOpt === optText;
              return (
                <button
                  key={i}
                  onClick={() => !showFeedback && setSelectedOpt(optText)}
                  disabled={!!showFeedback}
                  className={`px-4 py-2 rounded-xl font-headline font-bold text-sm transition-all border-2 ${
                    isSelected
                      ? 'bg-hupath-scout text-white border-hupath-scout'
                      : 'bg-white text-hp-on-surface border-gray-200 hover:border-hupath-scout'
                  } disabled:opacity-60`}
                >
                  {optText}
                </button>
              );
            })}
          </div>
        ) : (
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !showFeedback && handleSubmit()}
            disabled={!!showFeedback}
            placeholder="Nhập đáp án..."
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-hupath-scout outline-none font-headline font-bold"
          />
        )}
      </div>

      {/* Feedback */}
      {showFeedback && (
        <div className={`hp-card hp-card--compact ${showFeedback.correct ? 'bg-green-50 border-l-8 border-green-500' : 'bg-red-50 border-l-8 border-red-500'}`}>
          <p className="font-headline font-black text-xl mb-1">
            {showFeedback.correct ? '✓ Chính xác!' : '✗ Chưa đúng'}
          </p>
          {!showFeedback.correct && (
            <p className="text-sm text-hp-on-surface/70">
              Đáp án: <span className="font-bold">{showFeedback.expected}</span>
            </p>
          )}
        </div>
      )}

      {/* Submit / Next button */}
      {!showFeedback ? (
        <button
          onClick={handleSubmit}
          disabled={!input && !selectedOpt}
          className="w-full hp-button-gradient text-white py-4 rounded-xl font-headline font-black text-lg shadow-lg hover:-translate-y-1 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Kiểm tra
        </button>
      ) : (
        <button
          onClick={handleNext}
          className="w-full hp-button-gradient text-white py-4 rounded-xl font-headline font-black text-lg shadow-lg hover:-translate-y-1 active:scale-95 transition-all"
        >
          {idx + 1 >= total ? 'Hoàn thành →' : 'Tiếp theo →'}
        </button>
      )}
    </div>
  );
};

export default ExerciseRunner;
